import React, { useState } from 'react';
import moment from 'moment';
import { Button, Modal } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { AlertResultExport } from '../../types/AlertResults';
import { FileFormat, ExportButton } from 'src/components/common/ExportButton';
import { ExportConfigurationTable } from './ExportConfigurationTable';
import { ColumnId, exportHeaders, defaultColumnIds } from './AlertResultExport.config';
import { getAlertResultExportList } from '../../api/alertResults.api';

type ModalProps = {
  visible: boolean;
  alertId: string;
  sourceOwnerId: string;
  insertionDate: string;
  className?: string;
  closeModal: () => void;
};

const getExportFileName = (ext: FileFormat) => {
  return `alert_results_${moment().format('YYYYMMDDThhmmss')}.${ext}`;
};

const extractor = <T,>(data: T) => {
  const results = (data as AlertResultExport.Response).alertResultExportList;
  return results as T;
};

const mapping =
  (selectedKeys: ColumnId[]) =>
  <T,>(item: T) => {
    const row = item as AlertResultExport.ListItem;
    return Object.fromEntries(selectedKeys.map((key) => [exportHeaders[key], row[key]])) as T;
  };

export const ExportConfigurationModal: React.FC<ModalProps> = ({
  visible,
  alertId,
  sourceOwnerId,
  insertionDate,
  className,
  closeModal,
}) => {
  const [name, setName] = useState<string>('');
  const [format, setFormat] = useState<FileFormat>('csv');
  const [selectedColumnIds, setSelectedColumnIds] = useState<ColumnId[]>(defaultColumnIds);
  const defaultName = getExportFileName(format);

  const params = {
    alertId,
    sourceOwnerId,
    createdDate: insertionDate,
  };

  const handleColumnsChange = (columnIds: CheckboxValueType[]) => {
    const selectedIds = columnIds.map(String) as ColumnId[];
    setSelectedColumnIds(selectedIds);
  };

  const renderModalFooter = () => (
    <>
      <Button onClick={closeModal}>Cancel</Button>
      <ExportButton
        fetcher={(token) => getAlertResultExportList(params, token)}
        format={format}
        extractor={extractor}
        fileName={name || defaultName}
        worksheetName={'Alert Results'}
        mapping={mapping(selectedColumnIds)}
        disabled={!selectedColumnIds.length}
        onStart={closeModal}
      />
    </>
  );

  return (
    <Modal
      className={className}
      title={'Export Configuration'}
      visible={visible}
      footer={renderModalFooter()}
      onCancel={closeModal}
    >
      <ExportConfigurationTable
        name={name}
        format={format}
        placeholder={defaultName}
        selectedColumns={selectedColumnIds}
        onNameChange={setName}
        onFormatChange={setFormat}
        onColumnsChange={handleColumnsChange}
      />
    </Modal>
  );
};
